// Audio framing for Otto Live Protocol v1.
//
// Audio travels as BINARY WebSocket messages with no header: raw PCM, signed
// 16-bit little endian, mono. The direction tells you the rate:
//   browser -> otto-live: the owner's mic at 16 kHz, one 20 ms frame per message
//   otto-live -> browser: Otto's voice at 24 kHz, any whole number of samples
//
// Which Otto turn a binary frame belongs to is carried by ORDER, not by a
// header: the server sends `otto_speaking {turn}` before the first frame of a
// turn and `otto_done {turn}` after the last. After `interrupted {turn}` the
// client drops any queued audio for that turn, and drops any frame that
// arrives before the next `otto_speaking`. WebSocket messages are ordered, so
// this is exact.
//
// 20 ms mic frames (design 3.2 row 4) replace today's ~85 ms frames and save
// up to about 45 ms of reply latency (ESTIMATE, research 07 section 7).

export const PCM_ENCODING = "pcm_s16le" as const;
export const AUDIO_CHANNELS = 1 as const;
export const BYTES_PER_SAMPLE = 2 as const;

export const MIC_SAMPLE_RATE = 16_000 as const;
export const MIC_FRAME_MS = 20 as const;
/** 16 000 samples/s * 0.020 s = 320 samples. */
export const MIC_FRAME_SAMPLES = (MIC_SAMPLE_RATE * MIC_FRAME_MS) / 1000;
/** 320 samples * 2 bytes = 640 bytes. */
export const MIC_FRAME_BYTES = MIC_FRAME_SAMPLES * BYTES_PER_SAMPLE;

/**
 * The largest mic message otto-live accepts: 100 ms (five frames), so a
 * browser that briefly batches after a stall is not cut off. Anything larger,
 * or an odd byte count, is dropped (design 9.4: oversize frames are dropped).
 */
export const MAX_MIC_MESSAGE_BYTES = MIC_FRAME_BYTES * 5;

export const OTTO_SAMPLE_RATE = 24_000 as const;

/** Largest JSON text message either side sends. Events are small by design. */
export const MAX_TEXT_MESSAGE_BYTES = 16 * 1024;

/** Duration of a PCM16 mono buffer in milliseconds. */
export function pcm16DurationMs(byteLength: number, sampleRate: number): number {
  if (byteLength <= 0 || sampleRate <= 0) return 0;
  return (byteLength / BYTES_PER_SAMPLE / sampleRate) * 1000;
}

/** True when a browser-to-server binary message is an acceptable mic payload. */
export function isAcceptableMicMessage(byteLength: number): boolean {
  return byteLength > 0 && byteLength % BYTES_PER_SAMPLE === 0 && byteLength <= MAX_MIC_MESSAGE_BYTES;
}
